<template>
  <div class="app-root">
    <slot />
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { authState } from '../lib/auth'
import { isBrowserAuthConfigured } from '../lib/supabase'

// The main idea is that this component provides info about running jobs to descendants in a reactive way. It does an
// apollo query for running jobs, as well as subscribes to updates for running jobs. So it will initially be in a
// loading state, and fetch the running jobs. However, whenever data from the subscription comes in that will be used.
// We use GraphQL query aliases to keep the two sets of runningJob GraphQL queries straight.
//
// Reminder: to accomplish the reactive nature of the provider, we use the vue-reactive-provide library.
export default {
  data() {
    return {
      error: null,
      fetchJobs: [],
      subscribeJobs: null,
      lastAlertId: null,
    }
  },
  reactiveProvide: {
    name: 'jobsProvider',
    include: ['loading', 'error', 'runningJobs']
  },
  apollo: {
    fetchJobs: {
      query: gql`query RunningJobs {
        fetchJobs: runningJobs {
          sourceName
          type
          startedAt
          status
          progress {
            phase
            resourceName
            destinationName
            message
            recordsProcessed
            page
          }
        }
      }`,
      skip() {
        return isBrowserAuthConfigured() && !authState.isAuthenticated
      },
      error(error) {
        this.error = error
      },
      result({ data }) {
        if (data) {
          this.error = null
        }
      },
    },
    $subscribe: {
      subscribeJobs: {
        query: gql`subscription {
          subscribeJobs: runningJobs {
            sourceName
            type
            startedAt
            status
            progress {
              phase
              resourceName
              destinationName
              message
              recordsProcessed
              page
            }
          }
        }`,
        skip() {
          return isBrowserAuthConfigured() && !authState.isAuthenticated
        },
        result({ data }) {
          this.subscribeJobs = data.subscribeJobs
          this.error = null
        },
        error(error) {
          // Don't blank the whole Jobs UI if only the live socket fails — HTTP poll still works
          console.warn('runningJobs subscription error', error)
        },
      },
      recentAlerts: {
        query: gql`subscription {
          recentAlerts {
            id
            createdAt
            level
            title
            message
            sourceName
            jobType
          }
        }`,
        skip() {
          return isBrowserAuthConfigured() && !authState.isAuthenticated
        },
        result({ data }) {
          const list = (data && data.recentAlerts) || []
          if (!list.length) return
          const newest = list[0]
          if (this.lastAlertId === newest.id) return
          // Skip the first snapshot after connect so we don't toast historical alerts
          if (this.lastAlertId == null) {
            this.lastAlertId = newest.id
            return
          }
          this.lastAlertId = newest.id
          const severity = newest.level === 'danger' ? 'error'
            : newest.level === 'success' ? 'success'
              : 'info'
          this.$toast.add({
            severity,
            summary: newest.title,
            detail: newest.message || newest.title,
            life: newest.level === 'danger' ? 8000 : 4000,
          })
        },
        error(error) {
          console.warn('recentAlerts subscription error', error)
        },
      },
    },
  },
  computed: {
    loading() {
      if (this.subscribeJobs || this.error) {
        return false
      }
      if (isBrowserAuthConfigured() && !authState.isAuthenticated) {
        return false
      }
      return this.$apollo.queries.fetchJobs.loading
    },
    runningJobs() {
      return this.subscribeJobs || this.fetchJobs
    },
  },
}
</script>

<style scoped>
.app-root {
  display: block;
  width: 100%;
  max-width: 100%;
  min-height: 100%;
}
</style>
